// external
import { useContext, useMemo } from "react";

// components
import AidResultsTable from "./aid-results-table";
import CustomButton from "components/partials/custom-button/custom-button";

// state management
import { FaoResultsContext } from "context/FaoResultsContext";
import useNonModeledHandlers from "./hooks/use-non-model-handlers";

// assets
import Icons from "../../assets/icons";

// constants
import { TABLE_HEADERS } from "./constants/results-table-headers.constants";

/**
 * Configuration for editable input cells in the table
 * Defines which fields can be edited and their input step values
 */
const INPUT_CELLS = [
  { key: "estTuitionDiscountRate", step: "0.01" }, // 0.01% increments for discount rate
  { key: "estEnrollment", step: "1" }, // Whole number increments for enrollment
];

/**
 * FaoNonModelData Component
 *
 * This component renders the Non-Modeled Aid Results interface, allowing users to:
 * - View and edit enrollment estimates for different student groups
 * - Adjust tuition discount rates for each group
 * - Compare current values with last year's data
 * - Reset individual groups or all data to original values
 * - Save changes to the backend
 *
 * Key Features:
 * - Interactive table with editable cells for key metrics
 * - Last year comparison toggle for data context
 * - Visual indicators for modified groups (green highlighting)
 * - Reset functionality at group and global levels
 * - Real-time save state management with user feedback
 * - Loading indicators during save operations
 *
 * The component integrates with the non-modeled data hook system to provide
 * seamless data management and user interaction handling.
 */
function FaoNonModelData() {
  // Get data and state from results context
  const { nonModeledData, isSaving, isSaved } = useContext(FaoResultsContext);

  // Get all handler functions for user interactions
  const {
    handleNonModeledInputChange,
    isPopulationAdjusted,
    isGroupAdjusted,
    handleReset,
    handleResetAll,
    handleNonModeledSave,
  } = useNonModeledHandlers();

  /**
   * Dynamically determines save button text based on current state
   * Provides clear feedback to users about the save operation status
   */
  const saveButtonText = useMemo(() => {
    if (isSaving.nonModeledData) return "Saving...";
    if (isSaved.nonModeledData) return "Saved";
    return "Save Non-Modeled Adjustments";
  }, [isSaving.nonModeledData, isSaved.nonModeledData]);

  return (
    <div className="flex flex-col items-center gap-4">
      {/* Main data table with full functionality */}
      <AidResultsTable
        // Core table data configuration
        displayData={{
          tableHeaders: TABLE_HEADERS,
          tableData: nonModeledData.adjusted, // Show current adjusted values
        }}
        // Editable functionality configuration
        editable={{
          isEditable: true,
          inputCells: INPUT_CELLS, // Define which cells are editable
          handleInputChange: handleNonModeledInputChange, // Input change handler
        }}
        // Reset functionality configuration
        reset={{
          resetToggle: true, // Enable reset buttons
          handleReset: handleReset, // Individual group reset
          isPopulationAdjusted: isPopulationAdjusted, // Check if any changes made
          isGroupAdjusted: isGroupAdjusted, // Check if specific group changed
          handleResetAll: handleResetAll, // Reset all groups
        }}
        // Last year comparison configuration
        lastYear={{
          lastYearToggle: true, // Enable last year toggle
          lastYearData: nonModeledData.base, // Base data for comparison
        }}
      />

      {/* Save button with dynamic state */}
      <CustomButton
        variant="primary"
        onClick={handleNonModeledSave}
        disabled={isSaving.nonModeledData} // Disable during save operation
        iconBefore={
          // Show spinner icon only during save operation
          isSaving.nonModeledData && (
            <Icons.Spinner customClass="h-full w-4 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-blue-900" />
          )
        }
      >
        {saveButtonText}
      </CustomButton>
    </div>
  );
}

export default FaoNonModelData;
